import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Collapse,
  ListItem,
  Tabs,
  Tab,
  Card,
} from '@mui/material';
import {
  MenuBook as MenuBookIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { useAdmin } from '../../contexts/AdminContext';
import { getReadingAnswers, deleteReadingAnswer, getPartnerAdminReadingAnswers } from '../../api/answersApi';
import { useGroupedData } from '../../hooks';
import {
  PageHeader,
  StatsCards,
  GroupedAccordionList,
  ModernButton,
} from '../../components/common';
import { format } from 'date-fns';

const ReadingAnswersManagement = () => {
  const { showToast } = useToast();
  const { isPlatformAdmin } = useAdmin();
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [currentTab, setCurrentTab] = useState('real');

  const filteredAnswers = answers.filter(answer => {
    const isPractice = answer.userProductRegistrationId?.productId?.isPractice || false;
    return currentTab === 'practice' ? isPractice : !isPractice;
  });

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setExpandedRows({});
  };

  // Use the grouped data hook
  const {
    groupsArray,
    isGroupExpanded,
    toggleGroup,
  } = useGroupedData({
    data: filteredAnswers,
    getGroupKey: (answer) => answer.userProductRegistrationId?.productId?._id || 'unknown',
    getGroupMeta: (answer) => ({
      examName: answer.userProductRegistrationId?.productId?.name || answer.userProductRegistrationId?.productId?.label || 'Unknown Exam',
      examDate: answer.userProductRegistrationId?.productId?.startDate,
    }),
    itemsKey: 'answers',
  });

  // Fetch data
  const fetchAnswers = useCallback(async () => {
    try {
      setLoading(true);
      // Use partner-scoped API for non-platform admins
      const { data } = isPlatformAdmin
        ? await getReadingAnswers()
        : await getPartnerAdminReadingAnswers();
      setAnswers(data.data || data || []);
    } catch (error) {
      console.error('Failed to fetch reading answers:', error);
      showToast('Failed to load reading answers', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, isPlatformAdmin]);

  useEffect(() => {
    fetchAnswers();
  }, [fetchAnswers]);

  // Helper functions
  const getStudentName = (answer) => {
    const user = answer.userProductRegistrationId?.userId;
    if (user?.firstName || user?.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    if (user?.name) return user.name;
    return 'N/A';
  };

  const getStudentEmail = (answer) => {
    return answer.userProductRegistrationId?.userId?.email || 'N/A';
  };

  const getCorrectAnswersCount = (answerList) => {
    return answerList.filter((a) => a.isCorrect).length;
  };

  const getAnsweredCount = (answerList) => {
    return answerList.filter((a) => a.answer !== null && a.answer !== '').length;
  };

  // Stats
  const stats = [
    {
      label: 'Total Submissions',
      value: answers.length,
      color: 'primary.main',
    },
  ];

  // Handlers
  const handleViewDetails = (answer) => {
    setSelectedAnswer(answer);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this answer record?')) {
      try {
        await deleteReadingAnswer(id);
        showToast('Answer record deleted successfully', 'success');
        fetchAnswers();
      } catch (error) {
        console.error('Failed to delete answer:', error);
        showToast('Failed to delete answer record', 'error');
      }
    }
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Render item for GroupedAccordionList
  const renderItem = (answer) => {
    const correctCount = getCorrectAnswersCount(answer.answers);
    const answeredCount = getAnsweredCount(answer.answers);
    const isExpanded = expandedRows[answer._id];

    return (
      <ListItem
        key={answer._id}
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          py: 2.5,
          px: 3,
          backgroundColor: 'background.paper',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {getStudentName(answer)}
              </Typography>
              <Chip
                label={`${answeredCount}/40`}
                color={answeredCount === 40 ? 'success' : 'default'}
                size="small"
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
              <Chip
                label={`${correctCount}/40 correct`}
                color={correctCount >= 30 ? 'success' : correctCount >= 20 ? 'warning' : 'error'}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
              <Chip
                label={`${((correctCount / 40) * 100).toFixed(1)}%`}
                color="primary"
                size="small"
                sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {getStudentEmail(answer)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Submitted: {format(new Date(answer.createdAt), 'dd-MM-yyyy HH:mm')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Expand Details">
              <IconButton
                size="small"
                onClick={() => toggleRow(answer._id)}
                sx={{
                  color: 'info.main',
                  '&:hover': { backgroundColor: 'info.50' },
                }}
              >
                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={() => handleViewDetails(answer)}
                sx={{
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.50' },
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isPlatformAdmin && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDelete(answer._id)}
                  sx={{
                    color: 'error.main',
                    '&:hover': { backgroundColor: 'error.50' },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Expanded Answer Details */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Question-by-Question Breakdown
            </Typography>
            <Grid container spacing={1}>
              {answer.answers.map((ans, ansIdx) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={ansIdx}>
                  <Paper
                    sx={{
                      p: 1.5,
                      textAlign: 'center',
                      backgroundColor: ans.isCorrect ? 'success.50' : ans.answer ? 'error.50' : 'grey.100',
                      border: 1,
                      borderColor: ans.isCorrect ? 'success.main' : ans.answer ? 'error.main' : 'grey.300',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                      Q{ans.questionNumber}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, my: 0.5 }}>
                      {ans.answer || '—'}
                    </Typography>
                    {ans.isCorrect ? (
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : ans.answer ? (
                      <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    ) : null}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Collapse>
      </ListItem>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Reading Answers Management"
        subtitle="View and manage all user answers for reading tests"
        icon={<MenuBookIcon />}
        iconColor="primary.main"
      />

      <StatsCards stats={stats} columns={1} />

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            label="Real Exam Answers"
            value="real"
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.938rem' }}
          />
          <Tab
            label="Practice Exam Answers"
            value="practice"
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.938rem' }}
          />
        </Tabs>
      </Card>

      <GroupedAccordionList
        groups={groupsArray}
        itemsKey="answers"
        isGroupExpanded={isGroupExpanded}
        onToggleGroup={toggleGroup}
        renderItem={renderItem}
        itemLabel="student"
      />

      {/* Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Reading Answer Details
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedAnswer && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Student Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {getStudentName(selectedAnswer)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {getStudentEmail(selectedAnswer)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Exam
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedAnswer.userProductRegistrationId?.productId?.name || selectedAnswer.userProductRegistrationId?.productId?.label || 'N/A'}
                  </Typography>
                  {selectedAnswer.userProductRegistrationId?.productId?.startDate && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {format(new Date(selectedAnswer.userProductRegistrationId.productId.startDate), 'dd-MM-yyyy HH:mm')}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Summary
              </Typography>
              <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                <Chip label={`Answered: ${getAnsweredCount(selectedAnswer.answers)}/40`} color="info" />
                <Chip label={`Correct: ${getCorrectAnswersCount(selectedAnswer.answers)}/40`} color="success" />
                <Chip
                  label={`Score: ${((getCorrectAnswersCount(selectedAnswer.answers) / 40) * 100).toFixed(1)}%`}
                  color="primary"
                />
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                All Answers
              </Typography>
              <Grid container spacing={1}>
                {selectedAnswer.answers.map((ans, idx) => (
                  <Grid item xs={6} sm={4} md={3} key={idx}>
                    <Paper
                      sx={{
                        p: 1.5,
                        textAlign: 'center',
                        backgroundColor: ans.isCorrect ? 'success.50' : ans.answer ? 'error.50' : 'grey.100',
                        border: 1,
                        borderColor: ans.isCorrect ? 'success.main' : ans.answer ? 'error.main' : 'grey.300',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                        Q{ans.questionNumber}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, my: 0.5 }}>
                        {ans.answer || '—'}
                      </Typography>
                      {ans.isCorrect ? (
                        <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      ) : ans.answer ? (
                        <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      ) : null}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <ModernButton onClick={() => setDialogOpen(false)} variant="outlined">
            Close
          </ModernButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReadingAnswersManagement;
